/**
 * RelicMemory — the signature Relikara mechanic.
 *
 * "Power comes from awakening memories." Every weapon and piece of armor keeps
 * a private ledger of deeds done in a hero's hands — enemies slain, depths
 * survived, lives it saved. As that memory accrues, the relic AWAKENS through
 * tiers, each granting escalating power. This is layered on TOP of rarity: a
 * humble Common that has been through a hundred battles remembers more than a
 * pristine Epic pulled fresh from a chest.
 *
 * Memory lives on the item instance for the duration of a run. Deeds are
 * recorded by the CombatSystem/Game; bonuses are read by the Player and shown
 * in the inventory sidebar.
 */

// Awakening tiers. `at` = memory score threshold. Bonuses: dmg = weapon damage
// multiplier bonus; def/hp = flat armor bonuses. `line` = the whisper shown
// when a plain relic crosses into this tier.
export const MEM_TIERS = [
  { name: "Dormant",   at: 0,   dmg: 0.00, def: 0, hp: 0,  color: "#8b8f98", line: null },
  { name: "Stirring",  at: 25,  dmg: 0.06, def: 1, hp: 6,  color: "#9fd0ff", line: "…it hums faintly in your grip." },
  { name: "Waking",    at: 80,  dmg: 0.14, def: 2, hp: 14, color: "#7bd645", line: "The relic begins to remember your hands." },
  { name: "Awakened",  at: 200, dmg: 0.24, def: 4, hp: 26, color: "#c77dff", line: "It moves before you think to move it." },
  { name: "Ascendant", at: 450, dmg: 0.38, def: 7, hp: 44, color: "#ffcf6f", line: "It remembers everything. Even you." },
];

// Deed → memory-point conversion.
const POINTS = { kill: 1, depth: 6, save: 3 };

/** Ensure an item carries a memory ledger (weapons + armor only). */
export function ensureMemory(item, heroId = null) {
  if (!item || (item.kind !== "weapon" && item.kind !== "armor")) return null;
  if (!item.memory) item.memory = { kills: 0, depth: 0, saves: 0, tierIdx: 0, hero: heroId };
  else if (heroId && !item.memory.hero) item.memory.hero = heroId;
  return item.memory;
}

export function memScore(m) {
  if (!m) return 0;
  return m.kills * POINTS.kill + m.depth * POINTS.depth + m.saves * POINTS.save;
}

function tierIndexFor(score) {
  let idx = 0;
  for (let i = 0; i < MEM_TIERS.length; i++) if (score >= MEM_TIERS[i].at) idx = i;
  return idx;
}

/** The current awakening tier of an item (Dormant if it has no memory). */
export function memTier(item) {
  const m = item && item.memory;
  return MEM_TIERS[m ? tierIndexFor(memScore(m)) : 0];
}

/** Progress toward the next tier, 0..1 (1 if already at the peak). */
export function memProgress(item) {
  const m = item && item.memory; if (!m) return 0;
  const s = memScore(m);
  const idx = tierIndexFor(s);
  if (idx >= MEM_TIERS.length - 1) return 1;
  const lo = MEM_TIERS[idx].at, hi = MEM_TIERS[idx + 1].at;
  return Math.max(0, Math.min(1, (s - lo) / (hi - lo)));
}

/** Weapon damage multiplier from awakened memory. */
export function memWeaponMult(item) {
  if (!item || item.kind !== "weapon") return 1;
  return 1 + memTier(item).dmg;
}

/** Flat armor bonuses from awakened memory. */
export function memArmorBonus(item) {
  if (!item || item.kind !== "armor") return { def: 0, hp: 0 };
  const t = memTier(item);
  return { def: t.def, hp: t.hp };
}

// Record a deed. Returns the newly-reached tier object if the item ADVANCED a
// tier this call (so the caller can play a whisper/awakening VFX), else null.
function _record(item, field, n, heroId) {
  const m = ensureMemory(item, heroId); if (!m) return null;
  const before = tierIndexFor(memScore(m));
  m[field] += n;
  const after = tierIndexFor(memScore(m));
  m.tierIdx = after;
  return after > before ? MEM_TIERS[after] : null;
}

export const recordKill  = (item, n = 1, heroId) => _record(item, "kills", n, heroId);
export const recordDepth = (item, heroId)        => _record(item, "depth", 1, heroId);
export const recordSave  = (item, heroId)        => _record(item, "saves", 1, heroId);

/** One-line memory summary for tooltips, e.g. "Awakened · 143 slain, 4 depths". */
export function memSummary(item) {
  const m = item && item.memory; if (!m) return "";
  const t = memTier(item);
  const parts = [];
  if (m.kills) parts.push(`${m.kills} slain`);
  if (m.depth) parts.push(`${m.depth} depth${m.depth === 1 ? "" : "s"}`);
  if (m.saves) parts.push(`${m.saves} life-save${m.saves === 1 ? "" : "s"}`);
  return `${t.name}${parts.length ? " · " + parts.join(", ") : ""}`;
}
